import React, { useRef } from "react";
import {
  BottomSheetTextInput,
  useBottomSheet,
  useBottomSheetInternal,
} from "@gorhom/bottom-sheet";
import {
  AHIcon,
  TouchableBox,
  useAHTextTypeStyles,
  useLayoutContext,
  useTheme,
} from "arkh";
import Animated, {
  SharedValue,
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useDerivedValue,
} from "react-native-reanimated";
import { TextInput } from "react-native-gesture-handler";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { View } from "react-native";

export function TodoBottomSheetTitleInput(props: {
  title: string;
  setTitle: (title: string) => void;
  height: number;
  inputFocused: boolean;
  inputExpanded: SharedValue<number>;
  setInputFocused: (focused: boolean) => void;
  mode: "create" | "edit";
  bottomSheetWidth: number;
  editable?: boolean;
}) {
  const layout = useLayoutContext();
  const theme = useTheme();
  const inputRef = useRef<TextInput>(null);
  const intl_tokens = useIntlTokens();
  const bottomSheet = useBottomSheet();
  const bottomSheetInternals = useBottomSheetInternal();
  const inputTextStyle = useAHTextTypeStyles("title-small");

  const textInputVisible = useDerivedValue(() =>
    props.inputFocused || props.title.length > 0
      ? 1
      : Math.max(0, bottomSheetInternals.animatedIndex.value)
  );

  const animatedWidth = useAnimatedStyle(() => {
    return {
      width: interpolate(
        textInputVisible.value,
        [0, 1],
        [0, props.bottomSheetWidth]
      ),
    };
  });

  const animatedOpacity = useAnimatedStyle(
    () => ({
      opacity: interpolate(textInputVisible.value, [0, 0.5, 1], [0, 0, 1]),
    }),
    []
  );

  return (
    <Animated.View
      style={[
        {
          position: "absolute",
          top: 0,
          left: 0,
          height: props.height,
          padding: 1,
          alignItems: "center",
          backgroundColor: theme.surface,
        },
        props.mode === "create"
          ? animatedWidth
          : {
              width: props.bottomSheetWidth,
            },
      ]}
    >
      <Animated.View
        style={[
          {
            width: "100%",
          },
          props.mode === "create"
            ? animatedOpacity
            : {
                opacity: 1,
              },
        ]}
      >
        <BottomSheetTextInput
          ref={inputRef}
          editable={props.editable}
          onFocus={() => {
            props.setInputFocused(true);
          }}
          onBlur={() => {
            props.setInputFocused(false);
          }}
          style={{
            paddingRight: 20 + layout.space.medium + layout.space.small,
            paddingLeft: 20 + layout.space.medium + layout.space.small,
            height: props.height - 2,
            width: "100%",
            ...inputTextStyle,
          }}
          value={props.title}
          onChangeText={props.setTitle}
          placeholder={intl_tokens.Title}
          placeholderTextColor={theme["text-washed"]}
        />
      </Animated.View>
      <TodoBottomSheetTitleInputButton
        onPress={() => {
          bottomSheet.expand();
          inputRef.current?.focus();
        }}
        disabled={!props.editable}
        height={props.height}
        mode={props.mode}
        title={props.title}
        inputFocused={props.inputFocused}
      />
      <TodoBottomSheetTitleInputClear
        height={props.height}
        onClear={() => props.setTitle("")}
        show={
          props.mode === "create"
            ? props.title.length > 0
            : props.title.length > 0 && props.inputFocused
        }
      />
    </Animated.View>
  );
}

function TodoBottomSheetTitleInputClear(props: {
  show: boolean;
  onClear: () => void;
  height: number;
}) {
  const layout = useLayoutContext();
  return (
    <View
      style={[
        {
          position: "absolute",
          right: layout.space.medium,
          top: 0,
          zIndex: 100,
          justifyContent: "center",
          width: props.show ? layout.circle.medium : 0,
          opacity: props.show ? 1 : 0,
        },
      ]}
    >
      <TouchableBox
        disabled={!props.show}
        onPress={() => props.onClear()}
        style={{
          height: props.height,
          width: layout.circle.medium,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <AHIcon type="clear" color="text-secondary" />
      </TouchableBox>
    </View>
  );
}

function TodoBottomSheetTitleInputButton(props: {
  disabled?: boolean;
  height: number;
  mode: "create" | "edit";
  onPress: () => void;
  title: string;
  inputFocused: boolean;
}) {
  const layout = useLayoutContext();
  const theme = useTheme();
  const bottomSheetInternals = useBottomSheetInternal();

  const animation = useDerivedValue(() => {
    return props.inputFocused || props.title.length > 0
      ? 1
      : bottomSheetInternals.animatedIndex.value;
  });

  const iconStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateX: interpolate(animation.value, [0, 1], [9, -1]),
        },
      ],
    };
  });

  const animatedStyle = useAnimatedStyle(() => {
    return {
      width: interpolate(animation.value, [0, 1], [layout.circle.large, 20]),
      borderColor: interpolateColor(
        animation.value,
        [0, 0.2, 1],
        [theme.primary, "transparent", "transparent"]
      ),
    };
  });

  return (
    <Animated.View
      style={[
        {
          position: "absolute",
          left: layout.space.medium,
          height: layout.circle.large,
          width: layout.circle.large,
          borderRadius: layout.radius.medium,
          borderWidth: 1,
          top: (props.height - layout.circle.large) / 2,
          justifyContent: "center",
        },
        props.mode === "edit"
          ? {
              borderColor: "transparent",
            }
          : animatedStyle,
      ]}
    >
      <TouchableBox
        onPress={props.onPress}
        style={{ flex: 1, justifyContent: "center" }}
        disabled={props.disabled}
      >
        <Animated.View
          style={[
            { width: 20 },
            props.mode === "create"
              ? iconStyle
              : {
                  transform: [
                    {
                      translateX: -1,
                    },
                  ],
                },
          ]}
        >
          <AHIcon
            type="text-edit"
            color={
              props.inputFocused || props.title.length === 0
                ? "primary"
                : "text-primary"
            }
            forceSize={20}
          />
        </Animated.View>
      </TouchableBox>
    </Animated.View>
  );
}
